import httpClient from './httpClient';
import { API_CONFIG, STORAGE_KEYS } from '../config/api';
import {
  getSelectedCompany as getStoredCompany,
  setSelectedCompany as storeSelectedCompany,
  clearAllCompanyStorage,
} from '../utils/companyStorage';
import { getEmpresaIdFromUrl } from '../utils/companyUrl';

class AuthService {
  /**
   * Iniciar sesión
   * @param {string} email 
   * @param {string} password 
   * @returns {Promise}
   */
  async login(email, password) {
    console.log('🔐 AuthService.login() - Iniciando proceso de login');
    console.log('📧 Email:', email);
    console.log('🌐 URL de login:', `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.LOGIN}`);
    
    try {
      console.log('📤 Enviando petición de login...');
      
      const response = await httpClient.post(API_CONFIG.ENDPOINTS.AUTH.LOGIN, {
        email,
        password
      });

      console.log('📥 Respuesta recibida del servidor:');
      console.log('📊 Status:', response.status);
      console.log('📋 Headers:', response.headers);
      console.log('💾 Data completa:', response.data);

      const { data } = response;
      
      if (data.status === 'success') {
        // Guardar token, usuario, roles y permisos en localStorage
        const { token } = data.authorization;
        const { user, roles, permissions } = data;

        console.log('✅ Login exitoso!');
        console.log('👤 Usuario recibido:', user);
        console.log('🎭 Roles recibidos:', roles);
        console.log('🔐 Permisos recibidos:', permissions);
        console.log('🔑 Token recibido:', token ? 'Token presente (oculto por seguridad)' : 'NO HAY TOKEN');

        // Crear objeto usuario completo con roles y permisos
        const userComplete = {
          ...user,
          roles: roles || [],
          permissions: permissions || []
        };

        localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userComplete));
        localStorage.setItem(STORAGE_KEYS.ROLES, JSON.stringify(roles || []));
        localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(permissions || []));

        console.log('💾 Datos completos guardados en localStorage');

        return {
          success: true,
          user: userComplete,
          roles,
          permissions,
          token,
          message: 'Login exitoso'
        };
      } else {
        console.log('❌ Login falló - status no es success');
        console.log('📝 Mensaje del servidor:', data.message);
        throw new Error(data.message || 'Error en el login');
      }
    } catch (error) {
      console.log('🚨 ERROR en AuthService.login():');
      console.log('📍 Tipo de error:', error.name);
      console.log('💬 Mensaje:', error.message);
      
      if (error.response) {
        console.log('📡 Error de respuesta HTTP:');
        console.log('🔢 Status Code:', error.response.status);
        console.log('📄 Status Text:', error.response.statusText);
        console.log('💾 Response Data:', error.response.data);
        console.log('📋 Response Headers:', error.response.headers);
      } else if (error.request) {
        console.log('📡 Error de petición (sin respuesta):');
        console.log('🌐 Request:', error.request);
        console.log('⚠️ Posible problema: Servidor no responde o CORS');
      } else {
        console.log('⚙️ Error de configuración:', error.message);
      }

      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error de conexión'
      };
    }
  }

  /**
   * Registrar nuevo usuario
   * @param {Object} userData 
   * @returns {Promise}
   */
  async register(userData) {
    console.log('📝 AuthService.register() - Iniciando registro');
    console.log('👤 Datos del usuario:', userData);
    
    try {
      const response = await httpClient.post(API_CONFIG.ENDPOINTS.AUTH.REGISTER, userData);
      console.log('📥 Respuesta de registro:', response.data);
      
      const { data } = response;

      if (data.status === 'success') {
        // Guardar token, usuario, roles y permisos en localStorage
        const { token } = data.authorization;
        const { user, roles, permissions } = data;

        // Crear objeto usuario completo con roles y permisos
        const userComplete = {
          ...user,
          roles: roles || [],
          permissions: permissions || []
        };

        localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userComplete));
        localStorage.setItem(STORAGE_KEYS.ROLES, JSON.stringify(roles || []));
        localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(permissions || []));

        console.log('✅ Registro exitoso');

        return {
          success: true,
          user: userComplete,
          roles,
          permissions,
          token,
          message: data.message || 'Registro exitoso'
        };
      } else {
        throw new Error(data.message || 'Error en el registro');
      }
    } catch (error) {
      console.log('🚨 ERROR en AuthService.register():', error);
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error de conexión',
        errors: error.response?.data?.errors || {}
      };
    }
  }

  /**
   * Cerrar sesión
   * @returns {Promise}
   */
  async logout() {
    console.log('🚪 AuthService.logout() - Cerrando sesión');
    
    try {
      await httpClient.post(API_CONFIG.ENDPOINTS.AUTH.LOGOUT);
      console.log('✅ Logout exitoso en el servidor');
    } catch (error) {
      console.error('⚠️ Error al cerrar sesión en el servidor:', error);
    } finally {
      // Limpiar localStorage siempre, aunque falle la petición al servidor
      this.clearStorage();
      console.log('🧹 localStorage limpiado');
    }

    return {
      success: true,
      message: 'Sesión cerrada exitosamente'
    };
  }

  /**
   * Obtener información del usuario actual
   * @returns {Promise}
   */
  async getCurrentUser() {
    console.log('👤 AuthService.getCurrentUser() - Obteniendo usuario actual');
    
    try {
      const response = await httpClient.get(API_CONFIG.ENDPOINTS.AUTH.ME);
      const { data } = response;

      console.log('📥 Respuesta de /me:', data);

      if (data.status === 'success') {
        // Actualizar información del usuario, roles y permisos en localStorage
        const { user, roles, permissions } = data;
        
        // Crear objeto usuario completo con roles y permisos
        const userComplete = {
          ...user,
          roles: roles || [],
          permissions: permissions || []
        };

        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userComplete));
        localStorage.setItem(STORAGE_KEYS.ROLES, JSON.stringify(roles || []));
        localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(permissions || []));
        
        console.log('✅ Usuario actualizado con roles y permisos');
        
        return {
          success: true,
          user: userComplete,
          roles,
          permissions
        };
      } else {
        throw new Error(data.message || 'Error al obtener usuario');
      }
    } catch (error) {
      console.log('🚨 ERROR en AuthService.getCurrentUser():', error);
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error de conexión'
      };
    }
  }

  /**
   * Renovar token (Sanctum)
   * @returns {Promise}
   */
  async refreshToken() {
    console.log('🔄 AuthService.refreshToken() - Renovando token con Sanctum');
    
    try {
      const response = await httpClient.post(API_CONFIG.ENDPOINTS.AUTH.REFRESH);
      const { data } = response;

      console.log('📥 Respuesta de refresh:', data);

      if (data.status === 'success') {
        const { token } = data.authorization;
        localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, token);
        
        console.log('✅ Token renovado exitosamente');
        
        return {
          success: true,
          token,
          message: 'Token renovado exitosamente'
        };
      } else {
        throw new Error(data.message || 'Error al renovar token');
      }
    } catch (error) {
      console.log('🚨 ERROR en AuthService.refreshToken():', error);
      
      // Si no se puede renovar, limpiar todo
      this.clearStorage();
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error de conexión'
      };
    }
  }

  /**
   * Verificar si el usuario está autenticado
   * @returns {boolean}
   */
  isAuthenticated() {
    const token = localStorage.getItem(STORAGE_KEYS.JWT_TOKEN);
    const user = localStorage.getItem(STORAGE_KEYS.USER);
    const isAuth = !!(token && user);
    
    console.log('🔍 AuthService.isAuthenticated():', isAuth);
    console.log('🔑 Token presente:', !!token);
    console.log('👤 Usuario presente:', !!user);
    
    return isAuth;
  }

  /**
   * Obtener token JWT del localStorage
   * @returns {string|null}
   */
  getToken() {
    const token = localStorage.getItem(STORAGE_KEYS.JWT_TOKEN);
    console.log('🔑 AuthService.getToken():', token ? 'Token presente' : 'No hay token');
    return token;
  }

  /**
   * Obtener usuario del localStorage
   * @returns {Object|null}
   */
  getUser() {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    const user = userStr ? JSON.parse(userStr) : null;
    console.log('👤 AuthService.getUser():', user ? `Usuario: ${user.email}` : 'No hay usuario');
    return user;
  }

  /**
   * Obtener roles del localStorage
   * @returns {Array}
   */
  getRoles() {
    const rolesStr = localStorage.getItem(STORAGE_KEYS.ROLES);
    const roles = rolesStr ? JSON.parse(rolesStr) : [];
    console.log('🎭 AuthService.getRoles():', roles.length > 0 ? `${roles.length} roles` : 'No hay roles');
    return roles;
  }

  /**
   * Obtener permisos del localStorage
   * @returns {Array}
   */
  getPermissions() {
    const permissionsStr = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    const permissions = permissionsStr ? JSON.parse(permissionsStr) : [];
    console.log('🔐 AuthService.getPermissions():', permissions.length > 0 ? `${permissions.length} permisos` : 'No hay permisos');
    return permissions;
  }

  /**
   * ID de empresa activa desde la URL (?empresa=ID)
   * @returns {Object|null}
   */
  getSelectedCompany() {
    const id = getEmpresaIdFromUrl();
    if (!id) return null;
    const fromStorage = getStoredCompany(this.getUser()?.id ?? null);
    if (fromStorage?.id && Number(fromStorage.id) === Number(id)) {
      return fromStorage;
    }
    return { id };
  }

  /**
   * Respaldo opcional en storage; la URL es la fuente de verdad.
   * @param {Object} company
   */
  setSelectedCompany(company) {
    const user = this.getUser();
    storeSelectedCompany(company, user?.id ?? null);
  }

  /**
   * Limpiar toda la información del localStorage
   */
  clearStorage() {
    console.log('🧹 AuthService.clearStorage() - Limpiando localStorage');
    const user = this.getUser();
    clearAllCompanyStorage(user?.id ?? null);
    localStorage.removeItem(STORAGE_KEYS.JWT_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.ROLES);
    localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
    console.log('✅ localStorage limpiado completamente');
  }
}

// Exportar instancia única del servicio
const authService = new AuthService();
export default authService; 